import Button from "../../button/Button";
import styles from "./CancelSubscriptionOfferModal.module.scss";
import close from "../../../assets/icons/close.svg";
import fortect from "../../../assets/icons/fortect-shield.svg";
import off from "../../../assets/icons/off.svg";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscriptionData } from "../../../types/details-api-types";
import { useSetRenewalSubscriptionMutation } from "../../../store/services/details-api";
import { formatPriceWithCurrency } from "../../../utils";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";

interface CancelSubscriptionOfferModalProps {
  subscriptionItem: subscriptionData;
  callbackAction: (action: "deactivate" | "close" | "cancel", data?: subscriptionData) => void;
}

const CancelSubscriptionOfferModal: React.FC<CancelSubscriptionOfferModalProps> = ({
  callbackAction,
  subscriptionItem,
}) => {
  const [setRenewalSubscription] = useSetRenewalSubscriptionMutation();

  const renewalData = useMemo(() => {
    const totalCharges =
      subscriptionItem?.invoices?.reduce((total, invoice) => {
        if (invoice?.type?.toLowerCase() === "charge") {
          return Number(total.toFixed(2)) + Number(invoice.priceTotal);
        }
        return Number(total.toFixed(2));
      }, 0) || 0;

          const renewalPrice = Number(subscriptionItem?.renewalPrice || 0);

    const discountPercent =
      totalCharges > 0 ? Number((((renewalPrice - totalCharges) / renewalPrice) * 100).toFixed(2)) : 0;


    return {
      price: Number(totalCharges.toFixed(2)),
      currency: subscriptionItem?.renewalPriceCurrency || "",
      date: subscriptionItem?.next_payment ? new Date(subscriptionItem.next_payment) : null,
      percentage: discountPercent > 0 ? discountPercent : 60,
    };
  }, [subscriptionItem]);

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const showProcessingModal = async () => {
    setIsLoading(true);
    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_DISCOUNT_ACCEPTED);

    await setRenewalSubscription({ subscription_id: subscriptionItem?.subscription_id }).unwrap();
    setSuccess(true);
    setIsLoading(false);
  };

  const closeModal = () => {
    callbackAction("close", subscriptionItem);
    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_DISCOUNT_DECLINED);
  };

  const noTrust = () => {
    callbackAction("deactivate", subscriptionItem);
    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_DISCOUNT_DECLINED, {
      subscriptionId: subscriptionItem?.subscription_id,
    });
  };

  useEffect(() => {
    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_DISCOUNT_OFFER, {
      subscriptionId: subscriptionItem?.subscription_id,
    });
  }, [subscriptionItem]);

  return (
    <>
      {success ? (
        <div className={styles.root_success}>
          <div className={styles.root_close} onClick={() => closeModal()}>
            <img src={close} alt="close" />
          </div>

          <div className={styles.root_header}>
            <div className={styles.root_header_logo}>
              <img src={fortect} alt="fortect" />
            </div>
            <p className={styles.root_header_title}>{t("Success! Your Price Is Locked In")}</p>
            <p className={styles.root_header_paragraph}>{t("Your 60% discount has been applied.").replace("60", renewalData?.percentage.toString())}</p>
          </div>

          <div className={styles.root_list}>
            <ul className={styles.root_list_ul}>
              <li>{t("Renew Date: {{date}}", { date: renewalData?.date?.toLocaleDateString() })}</li>
              <li>
                {t("Renewal Price: {{price}}", {
                  price: formatPriceWithCurrency(renewalData?.price?.toString(), renewalData?.currency),
                })}
              </li>
            </ul>

            <div>
              <p className={styles.root_list_paragraph}>
                {t("Thank you for continuing to trust us with your digital security!")}
              </p>
            </div>
          </div>

          <div className={styles.root_footer}>
            <Button
              onClick={() => closeModal()}
              text={t("Close")}
              type="primary"
              classNames={styles.root_footer_okMobile}
            />
          </div>
        </div>
      ) : (
        <div className={styles.root}>
          {isLoading && (
            <div
              style={{
                zIndex: 9999,
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
              }}
            ></div>
          )}

          <div className={styles.root_badge}>
            <img src={off} alt="fortect" />
            <span>{t("60% OFF!").replace("60", renewalData?.percentage.toString())}</span>
          </div>

          <div className={styles.root_close} onClick={() => closeModal()}>
            <img src={close} alt="close" />
          </div>

          <div className={styles.root_header}>
            <div className={styles.root_header_logo}>
              <img src={fortect} alt="fortect" />
            </div>
            <p className={styles.root_header_title}>{t("Wait! Keep Your Last Year's Price.")}</p>
            <p className={styles.root_header_title}>{t("Save 60%").replace("60", renewalData?.percentage.toString())}.</p>
            <p className={styles.root_header_paragraph}>
              {t("Looking to keep your premium subscription at last year's rates? Unlock access & continue to enjoy:")}
            </p>
          </div>

          <div className={styles.root_list}>
            <ul className={styles.root_list_ul}>
              <li>{t("Full access to all current features")}</li>
              <li>{t("Microsoft-certified, award-winning antivirus")}</li>
              <li>{t("Real-time Security & Browsing Protection - Windows, Android & iOS")}</li>
            </ul>

            <div>
              <p className={styles.root_list_paragraph}>
                {t("We'll process your renewal at the exact price as last year")}
              </p>
              <p className={styles.root_list_paragraph__bold}>
                {t("Renew at only {{price}}", {
                  price: formatPriceWithCurrency(renewalData?.price?.toString(), renewalData?.currency),
                })}
              </p>
            </div>
          </div>

          <div className={styles.root_footer}>
            <Button
              onClick={() => showProcessingModal()}
              text={t("Lock In My Original Price")}
              type="primary"
              classNames={styles.root_footer_okMobile}
              isLoader={isLoading}
            />
            <Button
              onClick={() => noTrust()}
              text={t("No Thanks, Cancel Anyway")}
              type="error"
              classNames={styles.root_footer_cancelMobile}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CancelSubscriptionOfferModal;
